<template>
  <div :class="classResolver(classes)" class="quick-links-tile">
    <div class="quick-links-tile__head"><h3>{{ title }}</h3></div>

    <div class="quick-links-tile__list" :style="{ '--ql-cols': columns }">
      <CygnusIcon v-if="loading" name="spinner" spin class="quick-links-tile__loading" />
      <template v-else-if="links.length">
        <a
          v-for="link in links"
          :key="link.id ?? link.route"
          class="quick-links-tile__row"
          :href="isExternal(link.route) ? link.route : undefined"
          @click.prevent="follow(link.route)"
        >
          <span v-if="link.icon" class="quick-links-tile__icon"><CygnusIcon :name="link.icon" /></span>
          <span class="quick-links-tile__body">
            <span class="quick-links-tile__title">{{ link.label }}</span>
            <span v-if="link.description" class="quick-links-tile__desc">{{ link.description }}</span>
          </span>
          <CygnusIcon name="chevron-right" class="quick-links-tile__arrow" />
        </a>
      </template>
      <div v-else class="quick-links-tile__empty text-muted">No quick links configured.</div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import commonProps from '@apexl/icarus-core/src/components/_properties/common';
import classResolver from '@apexl/icarus-core/src/services/classResolver';
import useApi from '@apexl/icarus-core/src/services/api';
import CygnusIcon from '@apexl/icarus-core/src/components/icons/CygnusIcon.vue';

// Icarus counterpart of `apexl\Io\modules\dynamicComponents\Component\Tiles\QuickLinksTile`
// (registry NAME `QuickLinksTile`). Per that class's docblock, this tile
// doesn't receive the resolved link list as a prop — it calls
// `GET /api/v1/me/quick-links?route=…` itself so per-user pin operations
// can refresh without a full page re-render. Row anatomy (icon chip + title
// + description + arrow, single column) follows the project's own design
// system reference (`design/components/quick-links-tile.html`).
//
// That endpoint is live as of io-dynamic-components 1.0.3, fanning out to
// the `dashboard.collectQuickLinks` hook — this project's contribution is
// `app\module\shell\Hook\RegisterGoshawkQuickLinksHook` (Taxonomies,
// Permissions, Views, Page Layouts for route `home`). No contributions for
// a route is still a normal state (`{"links": []}`) — the empty state
// below, not an error.
const props = defineProps({
  ...commonProps,
  title: {
    type: String,
    default: 'Quick Links',
  },
  route: {
    type: String,
    default: 'home',
  },
  columns: {
    type: Number,
    default: 1,
  },
});

// `@apexl/icarus-core`'s `useApi()` axios instances carry no request
// timeout of their own — see `hooks/useDataSource.js` for the full
// rationale. An explicit timeout here keeps a stalled connection from
// hanging this tile (and the page) forever.
const REQUEST_TIMEOUT_MS = 8000;

const api = useApi();
const router = useRouter();
const links = ref([]);
const loading = ref(true);

function isExternal(route) {
  return /^https?:\/\//.test(route ?? '');
}

function follow(route) {
  if (isExternal(route)) {
    window.open(route, '_blank');
    return;
  }

  router.push(route);
}

onMounted(async () => {
  try {
    const { data } = await api.get('/api/v1/me/quick-links', { params: { route: props.route }, timeout: REQUEST_TIMEOUT_MS });
    links.value = Array.isArray(data?.links) ? data.links : [];
  } catch (error) {
    links.value = [];
  } finally {
    loading.value = false;
  }
});
</script>

<style lang="scss">
.quick-links-tile {
  background: var(--theme-bg-card);
  border: 1px solid var(--theme-border);
  border-radius: var(--theme-radius-lg, 10px);
  box-shadow: var(--theme-shadow-card);
  height: 100%;

  &__head {
    padding: 16px 18px 10px;
    border-bottom: 1px solid var(--theme-border-soft);

    h3 {
      margin: 0;
      font-size: 0.95rem;
      font-weight: 600;
      color: var(--theme-text-primary);
    }
  }

  &__list {
    display: grid;
    grid-template-columns: repeat(var(--ql-cols, 1), 1fr);
    padding: 4px 6px;
  }

  &__loading {
    padding: 12px;
    color: var(--theme-text-tertiary);
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 8px;
    border-radius: var(--theme-radius, 6px);
    text-decoration: none;
    color: inherit;
    cursor: pointer;

    &:hover {
      background: var(--theme-bg-content);
    }
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    border-radius: var(--theme-radius, 6px);
    background: var(--theme-accent-light);
    color: var(--theme-accent-dark);
  }

  &__body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  &__title {
    font-size: 0.83rem;
    font-weight: 600;
    color: var(--theme-text-primary);
  }

  &__desc {
    font-size: 0.72rem;
    color: var(--theme-text-tertiary);
    margin-top: 1px;
  }

  &__arrow {
    color: var(--theme-text-tertiary);
    flex-shrink: 0;
    font-size: 0.7rem;
  }

  &__empty {
    color: var(--theme-text-tertiary);
    padding: 8px 10px;
  }
}
</style>
